import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { MapProvider } from '../providers/map-provider';
import { ArcGisQueryRepositoryProvider } from '../../shared/providers/repository-providers';
import { PopupInteractionState } from '../states/popup-interaction-state';

// Importaciones de ArcGIS Core
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import Extent from '@arcgis/core/geometry/Extent';
import { LayerEntity } from '@gishub/domain/models/entities/layer-entity';
import { LayerTypeEnum } from '@gishub/domain';

@Injectable({ providedIn: 'root' })
export class PopupInteractionNotifier extends BaseNotifier<PopupInteractionState> {
  private _view: any = null;
  private _handler: { remove: () => void } | null = null;
  private _hoverHandler: { remove: () => void } | null = null;
  private _isPopupOpen = false;
  private _hoverHighlightLayer: GraphicsLayer | null = null;
  private _tooltipDiv: HTMLDivElement | null = null;
  private _noResultsTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly _hoverHighlightSymbol = new SimpleFillSymbol({
    color: [255, 170, 0, 0.20],
    outline: new SimpleLineSymbol({ color: [255, 120, 0], width: 3 }),
  });

  constructor(ref: Ref) {
    super(ref, { loading: false, enabled: false });

    this.ref.createEffect(() => {
      const view = this.ref.read(MapProvider).state().view;
      if (view !== this._view) {
        this._removeHandlers();
        this._view = view;
        if (view) {
          this._handler = view.on('click', (event: any) => this._handleClick(event, view));
          // Registramos pointer-move sin debounce para que sea ultra-sensible
          this._hoverHandler = view.on('pointer-move', (event: any) => this._handlePointerMove(event, view));
        }
      }
    });
  }

  private _removeHandlers(): void {
    if (this._handler) { this._handler.remove(); this._handler = null; }
    if (this._hoverHandler) { this._hoverHandler.remove(); this._hoverHandler = null; }
    this._clearHoverHighlight();
    this._view = null;
  }

  private _ensureHoverHighlightLayer(map: any): GraphicsLayer {
    if (!this._hoverHighlightLayer) {
      this._hoverHighlightLayer = new GraphicsLayer({ listMode: 'hide' });
      map.add(this._hoverHighlightLayer);
    }
    return this._hoverHighlightLayer;
  }

  private _ensureTooltip(): HTMLDivElement {
    if (!this._tooltipDiv) {
      let div = document.getElementById('observatorio-hover-tooltip') as HTMLDivElement;
      if (!div) {
        div = document.createElement('div');
        div.id = 'observatorio-hover-tooltip';
        div.style.position = 'fixed';
        div.style.display = 'none';
        div.style.background = 'rgba(33, 33, 33, 0.95)';
        div.style.border = '1px solid #ffaa00';
        div.style.padding = '6px 12px';
        div.style.borderRadius = '4px';
        div.style.fontFamily = 'Arial, sans-serif';
        div.style.fontSize = '13px';
        div.style.fontWeight = 'bold';
        div.style.color = '#ffffff';
        div.style.boxShadow = '0 3px 8px rgba(0,0,0,0.5)';
        div.style.pointerEvents = 'none'; // Clave para evitar parpadeos al pasar por encima
        div.style.zIndex = '99999';
        document.body.appendChild(div);
      }
      this._tooltipDiv = div;
    }
    return this._tooltipDiv;
  }

  private _hideTooltip(): void {
    if (this._tooltipDiv) {
      this._tooltipDiv.style.display = 'none';
    }
  }

  private _showTooltip(text: string, x: number, y: number): void {
    const tooltip = this._ensureTooltip();
    tooltip.innerHTML = text;
    tooltip.style.left = (x + 15) + 'px';
    tooltip.style.top = (y + 15) + 'px';
    tooltip.style.display = 'block';
  }

  private _clearHoverHighlight(): void {
    if (this._hoverHighlightLayer) {
      this._hoverHighlightLayer.removeAll();
    }
    this._hideTooltip();
  }

  setEnabled(enabled: boolean): void {
    this.updateState(s => ({ ...s, enabled }));
    if (!enabled && this._isPopupOpen) {
      const view = this.ref.read(MapProvider).state().view as any;
      if (view?.popup?.visible) view.popup.close();
      this._isPopupOpen = false;
    }
  }

  private _hoverSeq = 0;

  private async _handlePointerMove(event: any, view: any): Promise<void> {
    if (this.getStateValue().enabled) return;
    if (this._isPopupOpen) {
      this._clearHoverHighlight();
      return;
    }

    const seq = ++this._hoverSeq;

    try {
      // hitTest busca elementos gráficos renderizados directamente en el lienzo (WebGL) instantáneamente
      const response = await view.hitTest(event);
      if (seq !== this._hoverSeq) return;

      const results = response.results.filter((r: any) =>
        r.type === 'graphic' &&
        (r.layer?.title === 'Comunidades campesinas' || r.layer?.title === 'Comunidades nativas')
      );

      if (results.length > 0) {
        const graphic = results[0].graphic;
        const name = graphic.attributes?.nom_comunidad;

        if (name) {
          // 1. Resaltar la geometría de la comunidad (stand-out)
          const hlLayer = this._ensureHoverHighlightLayer(view.map);
          hlLayer.removeAll(); // Limpiar el highlight anterior
          hlLayer.add(new Graphic({
            geometry: graphic.geometry,
            symbol: this._hoverHighlightSymbol,
          }));

          // 2. Mostrar la etiqueta flotante (tooltip) en la posición de pantalla del puntero
          const clientX = event.native?.clientX ?? event.x;
          const clientY = event.native?.clientY ?? event.y;
          this._showTooltip(name, clientX, clientY);
          return;
        }
      }
    } catch (err) {
      console.error('[hover-hit-error]', err);
    }

    // Si no pasa por encima de ninguna comunidad, limpiamos
    this._clearHoverHighlight();
  }

  private async _handleClick(event: any, view: any): Promise<void> {
    if (!this.getStateValue().enabled) return;
    if (this._isPopupOpen && view.popup?.visible) return;

    if (this._noResultsTimer) {
      clearTimeout(this._noResultsTimer);
      this._noResultsTimer = null;
    }

    this.updateState(s => ({ ...s, loading: true }));

    const mapPoint = event.mapPoint;
    if (!mapPoint) { this.updateState(s => ({ ...s, loading: false })); return; }

    const map = this.ref.read(MapProvider).state().map;
    if (!map) { this.updateState(s => ({ ...s, loading: false })); return; }

    const queryRepo = this.ref.read(ArcGisQueryRepositoryProvider).instance;

    const visibleQueryTargets = this._getVisibleQueryTargets(map);
    if (visibleQueryTargets.length === 0) {
      this.updateState(s => ({ ...s, loading: false }));
      this._showNoResultsToast(mapPoint, view);
      return;
    }

    const extent = view.extent;
    const mapExtent = extent ? {
      xmin: extent.xmin, ymin: extent.ymin,
      xmax: extent.xmax, ymax: extent.ymax,
      spatialReference: { wkid: extent.spatialReference.wkid },
    } : undefined;

    const point = {
      x: mapPoint.x, y: mapPoint.y,
      spatialReference: { wkid: mapPoint.spatialReference.wkid },
    };
    const excludeFieldsLower = [
      'fid', 'objectid', 'shape', 'shape_length', 'shape_area',
      'shape.starea()', 'shape.stlength()', 'globalid',
      'creation_date', 'editor', 'edit_date'
    ];

    const allGraphics: Graphic[] = [];
    let graphicIndex = 0;

    let results: { target: LayerEntity; data: any }[];
    try {
      results = await Promise.all(
      visibleQueryTargets.map(async (target: LayerEntity) => {
        
        if (target.type === LayerTypeEnum.GeoJson && target.instance) {
          try {
            const query = target.instance.createQuery();

            // Creamos un bounding box de tolerancia basado en los pixeles de la vista actual
            // Esto asegura que la selección de líneas o puntos cargados por GeoJSON no falle por falta de precisión
            const tolerance = (view.resolution ?? 0) * 12;
            query.geometry = new Extent({
              xmin: mapPoint.x - tolerance,
              ymin: mapPoint.y - tolerance,
              xmax: mapPoint.x + tolerance,
              ymax: mapPoint.y + tolerance,
              spatialReference: mapPoint.spatialReference
            });

            query.spatialRelationship = 'intersects';
            query.outFields = ['*'];
            query.returnGeometry = true;

            const featureSet = await target.instance.queryFeatures(query);
            return {
              target,
              data: { success: true, data: featureSet.features || [] }
            };
          } catch (err) {
            console.error(`Error consultando localmente GeoJSONLayer [${target.name}]:`, err);
            return { target, data: { success: false, data: [] } };
          }
          } else {
            const r = await queryRepo.queryByPoint(target, point, view.resolution);
          return { target, data: r };
        }
      })
    );
    } catch {
      this.updateState(s => ({ ...s, loading: false }));
      this._showNoResultsToast(mapPoint, view);
      return;
    }

    for (const { target, data } of results) {
      if (!data.success || !data.data || data.data.length === 0) continue;

      for (const f of data.data) {
        const validFields = Object.keys(f.attributes).filter(k =>
          !excludeFieldsLower.includes(k.toLowerCase())
        );
        if (validFields.length === 0) continue;

        graphicIndex++;
        const uniqueAttributes = { ...f.attributes };
        uniqueAttributes.SYS_ID = graphicIndex;

        const g = new Graphic({
          attributes: uniqueAttributes,
          geometry: f.geometry
        });

        const titleField = validFields[0] || 'SYS_ID';

        g.popupTemplate = new PopupTemplate({
          title: `${target.name} - {${titleField}}`,
          content: [{
            type: 'fields',
            fieldInfos: validFields.map(k => ({
              fieldName: k,
              label: k,
              visible: true
            }))
          }]
        });

        allGraphics.push(g);
      }
    }

    // Inversiones locales (Chupaca): el GraphicsLayer 'intervenciones-locales' no es
    // consultable por query server-side, se resuelve con hitTest directo en el lienzo.
    try {
      const localResponse = await view.hitTest(event);
      for (const r of localResponse.results) {
        const localGraphic = (r as any)?.graphic;
        if (!localGraphic || localGraphic.layer?.id !== 'intervenciones-locales') continue;

        const validFields = Object.keys(localGraphic.attributes).filter(k =>
          !excludeFieldsLower.includes(k.toLowerCase())
        );
        if (validFields.length === 0) continue;

        graphicIndex++;
        const uniqueAttributes = { ...localGraphic.attributes };
        uniqueAttributes.SYS_ID = graphicIndex;

        const g = new Graphic({ attributes: uniqueAttributes, geometry: localGraphic.geometry });

        const titleField = validFields.find(f => f === 'nombre_intervencion') || validFields[0] || 'SYS_ID';

        g.popupTemplate = new PopupTemplate({
          title: `Intervención local - {${titleField}}`,
          content: [{
            type: 'fields',
            fieldInfos: validFields.map(k => ({
              fieldName: k,
              label: k,
              visible: true
            }))
          }]
        });

        allGraphics.push(g);
      }
    } catch (err) {
      console.error('[identify-local-hits-error]', err);
    }

    if (allGraphics.length === 0) {
      this.updateState(s => ({ ...s, loading: false }));
      this._showNoResultsToast(mapPoint, view);
      return;
    }

    this._isPopupOpen = true;
    view.popup.open({ location: mapPoint, features: allGraphics });
    this.updateState(s => ({ ...s, loading: false }));

    const closeHandler = view.popup.on('close', () => {
      this._isPopupOpen = false;
      closeHandler.remove();
    });
  }

  private _showNoResultsToast(location: any, view: any): void {
    if (this._noResultsTimer) {
      clearTimeout(this._noResultsTimer);
    }

    const popup = view.popup;
    if (!popup) return;

    const prevDock = popup.dockEnabled;
    popup.dockEnabled = false;

    popup.open({
      location,
      features: [
        new Graphic({
          attributes: { mensaje: 'No se encontraron elementos en esta ubicación' },
          popupTemplate: new PopupTemplate({
            title: 'Sin resultados',
            content: '{mensaje}',
          }),
        }),
      ],
    });

    this._noResultsTimer = setTimeout(() => {
      if (popup.visible) {
        popup.close();
      }
      popup.dockEnabled = prevDock;
      this._noResultsTimer = null;
    }, 3000);
  }

  /**
   * Rastrero recursivo del mapa. Filtra capas visibles y categoriza su origen operacional
   */
  private _getVisibleQueryTargets(map: any): any[] {
    const targets: LayerEntity[] = [];
    if (!map || !map.layers) return targets;

    map.layers.forEach((layer: any) => {
      if (layer.visible!==true) return;
      if (layer.popupEnabled === true && layer.declaredClass === 'esri.layers.FeatureLayer') {
          const entity = new LayerEntity({
            name: layer.title || layer.id,
            url: `${layer.url}/${layer.layerId}`,
            type: layer.layerType,
            visible: layer.visible,
            instance: layer,
          });
          targets.push(entity);
      }
      else if (layer.popupEnabled === true && layer.declaredClass === 'esri.layers.GeoJSONLayer') {
          const entity = new LayerEntity({
            name: layer.title || layer.id,
            url: layer.url,
            type: layer.layerType,
            visible: layer.visible,
            instance: layer,
          });
          targets.push(entity);
      }

      // 3. MapImageLayer (MapServer)
      else if (layer.popupEnabled === true && layer.declaredClass === 'esri.layers.MapImageLayer') {
        if (layer.sublayers) {
          const walkSublayers = (sublayers: any[]) => {
            sublayers.forEach((sub: any) => {
              if (!sub.visible) return;

              if (!sub.sublayers || sub.sublayers.length === 0) {
                if (sub.popupEnabled !== false) {
                  const entity = new LayerEntity({
                    name: sub.title || sub.name || layer.title,
                    url: `${layer.url}/${sub.id}`,
                    type: sub.layerType,
                    visible: sub.visible,
                    instance: layer,
                  });
                  targets.push(entity);
                }
              } else {
                const nextNodes = sub.sublayers.toArray ? sub.sublayers.toArray() : sub.sublayers;
                walkSublayers(nextNodes);
              }
            });
          };
          const rootSublayers = layer.sublayers.toArray ? layer.sublayers.toArray() : layer.sublayers;
          walkSublayers(rootSublayers);
        }
      }

      // 4. WMSLayer
      else if (layer.popupEnabled === true && layer.declaredClass === 'esri.layers.WMSLayer') {
        if (layer.sublayers) {
          const walkWms = (sublayers: any[]) => {
            sublayers.forEach((sub: any) => {
              if (!sub.visible) return;

              if (sub.visible === true && (!sub.sublayers || sub.sublayers.length === 0)) {
                const entity = new LayerEntity({
                  name: layer.title || layer.id,
                  url: layer.url,
                  type: layer.layerType,
                  visible: layer.visible,
                  instance: layer,
                });
                targets.push(entity);
              } else {
                const nextNodes = sub.sublayers.toArray ? sub.sublayers.toArray() : sub.sublayers;
                walkWms(nextNodes);
              }
            });
          };
          const rootWms = layer.sublayers.toArray ? layer.sublayers.toArray() : layer.sublayers;
          walkWms(rootWms);
        }
      }
    });

    return targets;
  }
}